import React from 'react';
import { Tooltip } from 'antd';

/**
 * Le resume d'un dossier, en une rangee de colonnes.
 *
 * POURQUOI PAS DES ETIQUETTES DE COULEUR. L'en-tete en alignait six, chacune de
 * sa couleur : statut, ET/TE, reprise, declaration validee, canal, liaison. Six
 * teintes cote a cote ne hierarchisent rien - elles se disputent le regard, et
 * l'oeil ne sait plus laquelle regarder en premier. Pire, la couleur y portait
 * deux sens a la fois : rouge disait tantot « corporel », tantot « rejete ».
 *
 * Une rangee de colonnes dit la meme chose sans crier. Chaque case annonce en
 * petit CE QU'ELLE CONTIENT, puis la valeur : le lecteur sait ou chercher, et
 * une case vide se voit aussi bien qu'une case pleine - ce que les etiquettes ne
 * savaient pas faire, puisqu'une etiquette absente ne laisse aucune trace.
 *
 * LES FILETS SONT DES INTERSTICES, PAS DES BORDURES. Le conteneur porte la
 * couleur du trait, la grille laisse un jour d'un pixel, et chaque case repose
 * sur son propre fond blanc. Des bordures par case se doubleraient entre
 * voisines, et il faudrait les retirer une a une sur les bords ; ici le trait se
 * replie tout seul quand la rangee passe a la ligne, horizontalement comme
 * verticalement.
 */

const BORDURE = '#e5e7eb';
const INK = '#111827';
const INK_MUTED = '#6b7280';
const INK_LIGHT = '#9ca3af';

/**
 * @param colonnes [{ cle, libelle, valeur, infobulle, onClick, accent }]
 *   `valeur` nulle ou vide rend un tiret : la colonne reste, car son absence de
 *   contenu est elle-meme une information.
 */
export default function ResumeEntete({ colonnes = [], style }) {
  const visibles = colonnes.filter(Boolean);
  if (!visibles.length) return null;

  return (
    <div
      style={{
        border: `1px solid ${BORDURE}`,
        borderRadius: 10,
        background: BORDURE,
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))',
        gap: 1,
        ...style,
      }}
    >
      {visibles.map((c) => {
        const vide = c.valeur === null || c.valeur === undefined || c.valeur === '';
        const cliquable = !!c.onClick && !vide;

        const contenu = (
          <div
            style={{
              background: '#fff',
              padding: '9px 14px',
              minWidth: 0,
              cursor: cliquable ? 'pointer' : 'default',
            }}
            onClick={cliquable ? c.onClick : undefined}
            onKeyDown={cliquable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); c.onClick(); } } : undefined}
            role={cliquable ? 'button' : undefined}
            tabIndex={cliquable ? 0 : undefined}
          >
            <div style={{
              fontSize: 11, color: INK_MUTED, lineHeight: 1.3, marginBottom: 3,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {c.libelle}
            </div>
            <div style={{
              fontSize: 13.5, fontWeight: 600, lineHeight: 1.35,
              color: vide ? INK_LIGHT : (c.accent || INK),
              textDecoration: cliquable ? 'underline' : 'none',
              textUnderlineOffset: 3,
              overflowWrap: 'anywhere',
            }}>
              {vide ? '-' : c.valeur}
            </div>
          </div>
        );

        return (
          <React.Fragment key={c.cle}>
            {c.infobulle ? <Tooltip title={c.infobulle}>{contenu}</Tooltip> : contenu}
          </React.Fragment>
        );
      })}
    </div>
  );
}
